import pick from 'lodash/pick';
import dayjs from 'utils/dayjs';
import ObjectUtils from '@spothero/utils/object';
import TimeUtils from 'utils/time';
import {
    buildCheckoutURL,
    buildImageTemplate,
    buildUniqueRateAmenities,
    formatAmenitySlug,
    formatDateTime,
    formatDistance,
    formatHoursOfOperation,
} from '../utils';
import {
    OBJECT_TRANSIENT_FACILITY_SUMMARY,
    OBJECT_TRANSIENT_FACILITY,
    OBJECT_TRANSIENT_RATE,
} from 'proptypes';

export function formatTransientRate({
    spotId,
    facilitySlug,
    quote: {order, advertisedPrice},
    additionalRateDetails,
    userRequestedStarts,
    userRequestedEnds,
    eventId,
    timezone,
    rebookId,
}) {
    // only keep properties that have corresponding rate type (transient, monthly, etc) prop types
    // and combine them with the additional rate details for that rate type as well as any additional
    // properties that rate type needs which are derived from other data not in the API

    const baseRate = order[0];
    const {rateId, starts: originalStarts, ends: originalEnds} = baseRate;
    const starts = formatDateTime(originalStarts, timezone);
    const ends = formatDateTime(originalEnds, timezone);

    const hasOversizedFee = baseRate.items.some(
        lineItem => lineItem.type === 'oversize_vehicle_fee_flat'
    );
    const {isExtended: isAutoExtended} = TimeUtils.getTimeExtension({
        actualStartDateTimeObj: dayjs(starts),
        actualEndDateTimeObj: dayjs(ends),
        requestedStartDateTimeObj: dayjs(userRequestedStarts),
        requestedEndDateTimeObj: dayjs(userRequestedEnds),
        isMonthly: false,
    });

    const formattedRate = pick(
        {
            ...additionalRateDetails,
            ...baseRate,
            starts: formatDateTime(baseRate.starts, timezone),
            ends: formatDateTime(baseRate.ends, timezone),
            priceBreakdown: baseRate.items,
            amenities: additionalRateDetails.amenities.map(formatAmenitySlug),
            fullUrl: buildCheckoutURL({
                spotId,
                facilitySlug,
                rateId,
                rateType: 'transient',
                starts: originalStarts,
                ends: originalEnds,
                eventId,
                timezone,
                rebookId,
            }),
            hasOversizedFee,
            isAutoExtended,
        },
        Object.keys(OBJECT_TRANSIENT_RATE)
    );

    return {
        ...formattedRate,
        advertisedPrice,
    };
}

/**
 * Formats data for a single transient facility
 *
 * @function formatTransientFacility
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientFacilityParking|Documentation}
 * @param {object} data - The untransformed data from the API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @param {object} formatOptions.userRequestedStarts - The start date/time that the request was made with
 * @param {object} formatOptions.userRequestedEnds - The end date/time that the request was made with
 * @param {string} formatOptions.eventId - The unique monolith ID corresponding to an event
 * @param {string} formatOptions.timezone - The timezone of the nearest city, used for formatting date/time
 * @param {string} formatOptions.rebookId - The ID of the original reservation that we are rebooking for
 * @param {boolean} formatOptions.isFacilitySummary - If true, will return the shortened version of the facility data (e.g. to display on spot cards)
 * @returns {object} The formatted transient facility data.
 */
export function formatTransientFacility(data, formatOptions = {}) {
    const {
        userRequestedStarts,
        userRequestedEnds,
        eventId,
        timezone,
        rebookId,
        isFacilitySummary,
    } = formatOptions;
    const dataFormat = isFacilitySummary
        ? OBJECT_TRANSIENT_FACILITY_SUMMARY
        : OBJECT_TRANSIENT_FACILITY;
    const facilityData = ObjectUtils.camelizeKeys(data);

    const {
        availability,
        distance,
        facility: {common, transient},
        rates: ratesResponse,
    } = facilityData;

    const {id: spotId, slug: facilitySlug, hoursOfOperation} = common;
    // only keep properties that have corresponding transient rate prop types
    const rates = ratesResponse.map(
        ({quote, transient: additionalRateDetails}) =>
            formatTransientRate({
                spotId,
                facilitySlug,
                quote,
                additionalRateDetails,
                userRequestedStarts,
                userRequestedEnds,
                eventId,
                timezone,
                rebookId,
            })
    );

    // only keep properties that have corresponding transient facility prop types
    return pick(
        {
            isV2: true,
            ...common,
            ...transient,
            images: common.images.map(buildImageTemplate),
            spotId, // spotId is relied on in a lot of places over id
            availability,
            distance: formatDistance(distance),
            rates,
            selectedRate: rates[0],
            allAmenities: buildUniqueRateAmenities(rates),
            hoursOfOperation: formatHoursOfOperation(hoursOfOperation),
        },
        Object.keys(dataFormat)
    );
}

/**
 * Formats data for multiple transient facilities
 *
 * @function formatTransientSeachResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientParking|Documentation}
 * @param {object} searchResults - Facility data returned from the search API for a multiple facilities
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {Array} The array of transient facilities, with data transformation applied.
 */
export function formatTransientSearchResponse(
    searchResults,
    formatOptions = {}
) {
    const formattedResults = searchResults.map(result =>
        formatTransientFacility(result, {
            ...formatOptions,
            isFacilitySummary: true,
        })
    );

    return formattedResults;
}

/**
 * Formats data for a single transient facility
 *
 * @function formatTransientSpotDetailsResponse
 * @see {@link https://api.spothero.com/v2/docs#operation/searchTransientFacilityParking|Documentation}
 * @param {object} facilityData - All the data returned from the search API for a single facility
 * @param {object} formatOptions - Additional options that don't get passed to the endpoint but are used for data transformation
 * @returns {object} The transient facility, with data transformation applied.
 */
export function formatTransientSpotDetailsResponse(
    facilityData,
    formatOptions
) {
    return formatTransientFacility(facilityData, {
        ...formatOptions,
        isFacilitySummary: false,
    });
}
